import React, { useEffect, useState } from "react";
import { FaSort, FaSortUp, FaSortDown, FaEye, FaTimes } from "react-icons/fa";
import ConfirmationModal from "../../components/modals/ConfirmationModal";
import Toast from "../../components/Toast/Toast";
import { getAllUsersAPI, updateUserStatusAPI } from "../../utils/api";
import Sidebar from "../Sidebar/Sidebar";
import UserDetailsModal from "./components/UserDetailsModal";
import "./WholesaleUsers.css";
import { useNavigate } from "react-router-dom";

const PAGE_LIMIT = 10;

const WholesaleUsers = () => {
  const navigate = useNavigate();

  const [users, setUsers] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [toast, setToast] = useState({ show: false, message: "", type: "" });

  // Sorting state
  const [sortField, setSortField] = useState("");
  const [sortOrder, setSortOrder] = useState("");

  const [pendingToggle, setPendingToggle] = useState(null);

  // User details modal state
  const [userDetailsModal, setUserDetailsModal] = useState(false);
  const [selectedUserForDetails, setSelectedUserForDetails] = useState(null);
  const [imagePreviewModal, setImagePreviewModal] = useState(false);
  const [selectedImageForPreview, setSelectedImageForPreview] = useState(null);

  const showToast = (message, type = "success") => {
    setToast({ show: true, message, type });
    setTimeout(() => setToast({ show: false, message: "", type: "" }), 3000);
  };

  const fetchUsers = async () => {
    try {
      setLoading(true);
      setError("");
      const response = await getAllUsersAPI({
        user_type: "wholeseller",
        page,
        limit: PAGE_LIMIT,
        search: searchTerm.trim().toLowerCase() || "",
      });

      if (response?.status === 200) {
        const list = response.data;
        const totalPagesFromApi = response?.pagination?.total_pages || 1;
        setUsers(list);
        setTotalPages(totalPagesFromApi);
      } else {
        setUsers([]);
        setTotalPages(1);
        setError(response?.message || "Failed to fetch wholesale users");
      }
    } catch (err) {
      setUsers([]);
      setTotalPages(1);
      setError("Failed to fetch wholesale users. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setPage(1);
  }, [searchTerm]);

  useEffect(() => {
    fetchUsers();
  }, [page, searchTerm]);

  // Sorting functionality
  const handleSort = (field) => {
    let newOrder = "asc";
    if (sortField === field) {
      newOrder = sortOrder === "asc" ? "desc" : "asc";
    }
    setSortField(field);
    setSortOrder(newOrder);
  };

  const getSortIcon = (field) => {
    if (sortField !== field) {
      return <FaSort />;
    }
    return sortOrder === "asc" ? <FaSortUp /> : <FaSortDown />;
  };

  const getSortedData = () => {
    if (!sortField || !sortOrder) {
      return users;
    }

    return [...users].sort((a, b) => {
      let aValue = a[sortField] || "";
      let bValue = b[sortField] || "";

      // Convert to lowercase for case-insensitive sorting
      aValue = aValue.toString().toLowerCase();
      bValue = bValue.toString().toLowerCase();

      if (sortOrder === "asc") {
        return aValue.localeCompare(bValue);
      } else {
        return bValue.localeCompare(aValue);
      }
    });
  };

  const startIdx = (page - 1) * PAGE_LIMIT;
  const endIdx = startIdx + users.length;

  const handleToggleClick = (userId, nextActive) => {
    setPendingToggle({ userId, nextActive });
  };

  const handleConfirmToggle = async () => {
    if (!pendingToggle) return;
    const { userId, nextActive } = pendingToggle;
    try {
      const reqData = { user_id: userId, status: nextActive ? 1 : 0 };
      const response = await updateUserStatusAPI(reqData);
      if (response?.status === 200) {
        setUsers((prev) =>
          prev.map((u) => (u._id === userId ? { ...u, active: nextActive ? 1 : 0 } : u))
        );
        showToast(`User ${nextActive ? "activated" : "deactivated"} successfully`);
      } else {
        showToast(response?.message || "Failed to update status", "error");
      }
    } catch (err) {
      showToast("Failed to update status", "error");
    } finally {
      setPendingToggle(null);
    }
  };

  const handleCancelToggle = () => setPendingToggle(null);

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) setPage(newPage);
  };

  const handleViewDetails = (user) => {
    setSelectedUserForDetails(user);
    setUserDetailsModal(true);
  };

  const handleCloseUserDetails = () => {
    setUserDetailsModal(false);
    setSelectedUserForDetails(null);
  };

  const handleImageClick = (imageUrl) => {
    setSelectedImageForPreview(imageUrl);
    setImagePreviewModal(true);
  };

  const handleCloseImagePreview = () => {
    setImagePreviewModal(false);
    setSelectedImageForPreview(null);
  };

  const handleUserClick = (userId) => {
    navigate(`/order?userId=${userId}`);
  };

  const sortedUsers = getSortedData();

  return (
    <>
      <Toast toast={toast} />
      <div className="WholesaleUsers-layout">
        <Sidebar />
        <div className="WholesaleUsers-main-content">
          <div className="WholesaleUsers-header-row">
            <input
              type="text"
              className="search-input"
              placeholder="Search by name or email"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              aria-label="Search wholesale users"
            />
          </div>

          {loading ? (
            <div className="loading-container">
              <div className="loading-spinner"></div>
              <p>Loading users...</p>
            </div>
          ) : error ? (
            <div className="loading-container">
              <div style={{ color: "#dc2626", fontSize: "1.25rem", marginBottom: "1rem" }}>⚠️</div>
              <p style={{ color: "#dc2626", fontSize: "1.125rem" }}>{error}</p>
            </div>
          ) : users.length === 0 ? (
            <div className="empty-state">
              <div style={{ fontSize: "3rem", marginBottom: "1rem" }}>👥</div>
              <h3>No Wholesale Users Found</h3>
              <p>There are no wholesale users in the system yet.</p>
            </div>
          ) : (
            <>
              <div className="table-responsive">
                <table className="WholesaleUsers-table">
                  <thead>
                    <tr>
                      <th>#</th>
                      <th 
                        className="sortable-header"
                        onClick={() => handleSort("name")}
                      >
                        <div className="header-content">
                          <span>NAME</span>
                          <span className="sort-icon">{getSortIcon("name")}</span>
                        </div>
                      </th>
                      <th 
                        className="sortable-header"
                        onClick={() => handleSort("email")}
                      >
                        <div className="header-content">
                          <span>EMAIL</span>
                          <span className="sort-icon">{getSortIcon("email")}</span>
                        </div>
                      </th>
                      <th>PHONE</th>
                      <th>ACTION</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sortedUsers.map((user, idx) => (
                      <tr 
                        key={typeof user._id === "object" ? JSON.stringify(user._id) : user._id || `user-${idx}`}
                        onClick={() => handleUserClick(user._id)}
                        style={{ cursor: 'pointer' }}
                      >
                        <td>{startIdx + idx + 1}</td>
                        <td>{user?.name || ""}</td>
                        <td>{user?.email || ""}</td>
                        <td>
                          {(user?.country_code || "") + " " + (user?.phoneno || "")}
                        </td>
                        <td onClick={(e) => e.stopPropagation()}>
                          <div className="wholesale-action-btn-row">
                            {(user?.gst || user?.pancard || user?.passport) && (
                              <button
                                type="button"
                                className="wholesale-action-btn wholesale-view-btn"
                                title="View Details"
                                onClick={() => handleViewDetails(user)}
                                aria-label="View Details"
                              >
                                <FaEye />
                              </button>
                            )}
                            <label className="switch">
                              <input
                                type="checkbox"
                                checked={!!user?.active}
                                onChange={(e) => handleToggleClick(user?._id, e.target.checked)}
                              />
                              <span className="slider" />
                            </label>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="table-footer">
                <div className="footer-left">
                  <span>
                    Showing {users.length > 0 ? startIdx + 1 : 0} to {endIdx} • Page {page} of {totalPages}
                  </span>
                </div>
                <div className="pagination">
                  <button type="button" disabled={page === 1} onClick={() => handlePageChange(page - 1)}>
                    Previous
                  </button>
                  {[...Array(totalPages).keys()].slice(0, 5).map((i) => (
                    <button
                      type="button"
                      key={i + 1}
                      className={page === i + 1 ? "active" : ""}
                      onClick={() => handlePageChange(i + 1)}
                    >
                      {i + 1}
                    </button>
                  ))}
                  {totalPages > 5 && <span className="ellipsis">…</span>}
                  <button type="button" disabled={page === totalPages} onClick={() => handlePageChange(page + 1)}>
                    Next
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      <ConfirmationModal
        visible={!!pendingToggle}
        onConfirm={handleConfirmToggle}
        onCancel={handleCancelToggle}
        title={pendingToggle?.nextActive ? "Activate User" : "Deactivate User"}
        message={`Are you sure you want to ${pendingToggle?.nextActive ? "activate" : "deactivate"} this user?`}
        confirmText={pendingToggle?.nextActive ? "Activate" : "Deactivate"}
      />

      {/* User Details Modal */}
      <UserDetailsModal
        user={selectedUserForDetails}
        isOpen={userDetailsModal}
        onClose={handleCloseUserDetails}
        onImageClick={handleImageClick}
      />

      {/* Image Preview Modal */}
      {imagePreviewModal && selectedImageForPreview && (
        <div className="modal-overlay" onClick={handleCloseImagePreview}>
          <div className="image-preview-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Document Preview</h3>
              <button className="modal-close" onClick={handleCloseImagePreview}>
                <FaTimes />
              </button>
            </div>
            <div className="modal-body">
              <img 
                src={selectedImageForPreview} 
                alt="Document Preview" 
                style={{ 
                  width: '100%', 
                  height: 'auto', 
                  maxHeight: '70vh',
                  objectFit: 'contain' 
                }} 
              />
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default WholesaleUsers;

